import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import {
  CalendarCheck, FileText, MessageSquare, ShieldCheck, Users, Scale,
  ArrowRight, Award, Clock, HeartHandshake, Lock, MapPin, Phone, Bot
} from "lucide-react";

const CATHEDRAL = "https://customer-assets-cm19k8pv.emergentagent.net/job_cafe-workflow-chat/artifacts/lycs4gkj_FB_IMG_1784570522837.jpg";

export default function Home() {
  const [news, setNews] = useState([]);
  const [hasToken, setHasToken] = useState(false);
  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data.news || []));
    setHasToken(!!getToken());
    const sync = () => setHasToken(!!getToken());
    window.addEventListener("labor-auth-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("labor-auth-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return (
    <div>
      {/* APRI CHAT - visibile solo a chi ha già fatto accesso */}
      {hasToken && (
        <button
          data-testid="home-open-chat"
          onClick={() => window.dispatchEvent(new Event("open-labor-ai"))}
          className="w-full group text-left bg-gradient-to-r from-labor-terracotta to-labor-terracottaDark text-black px-6 md:px-12 py-4 flex items-center gap-4 shadow-md hover:shadow-lg transition-shadow border-b-4 border-labor-warning"
        >
          <div className="w-11 h-11 bg-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
            <Bot className="w-6 h-6 text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl md:text-2xl font-bold leading-tight text-black">APRI CHAT</div>
            <div className="text-xs md:text-sm text-black/85">Chatta con Labor AI — 20 domande gratuite, poi 1-8 LP</div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-black">
            Apri <MessageSquare className="w-4 h-4" />
          </div>
        </button>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-labor-skyBg via-white to-labor-skyBg">
        <div className="absolute inset-0 opacity-[0.15] bg-cover bg-center" style={{ backgroundImage: `url(${CATHEDRAL})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent md:from-white md:via-white/85 md:to-white/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 bg-labor-olive/10 text-labor-olive px-3 py-1 text-xs uppercase tracking-widest font-semibold">
              <span className="w-2 h-2 bg-labor-italianGreen"></span>
              <span className="w-2 h-2 bg-white border border-labor-border"></span>
              <span className="w-2 h-2 bg-labor-italianRed"></span>
              Bitonto · Puglia
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-7xl leading-[1.02] text-labor-ink">
              Le tue pratiche,<br />
              seguite <em className="text-labor-terracotta not-italic">con cura</em>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-labor-muted max-w-2xl">
              CAF e Patronato a Bitonto. <strong className="text-labor-ink">Vito e Marianna</strong> ti aiutano su 730, ISEE, pensioni, invalidità, NASpI, CIE (tesserati) e successioni. Il mercoledì pomeriggio consulenze con l'<strong className="text-labor-ink">Avvocato</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/prenota" data-testid="home-cta-prenota" className="olive-btn px-6 py-4 font-semibold flex items-center gap-2">
                <CalendarCheck className="w-5 h-5" /> Prenota appuntamento
              </Link>
              <a href="tel:+393791466566" data-testid="home-cta-call" className="border-2 border-labor-ink text-labor-ink hover:bg-labor-ink hover:text-white transition-colors px-6 py-4 font-semibold flex items-center gap-2">
                <Phone className="w-4 h-4" /> 379 146 6566
              </a>
            </div>
          </div>
          <div className="md:col-span-4 paper-card p-6 md:p-8 bg-white/95 backdrop-blur-sm">
            <div className="text-xs uppercase tracking-widest text-labor-muted mb-3">Orari appuntamenti</div>
            <ul className="text-[15px] space-y-2">
              <li className="flex justify-between border-b border-labor-border pb-2"><span className="font-medium">Lunedì</span><span>9-12 · 16-18:30</span></li>
              <li className="flex justify-between border-b border-labor-border pb-2"><span className="font-medium">Martedì</span><span>16:00 - 18:30</span></li>
              <li className="flex justify-between border-b border-labor-border pb-2"><span className="font-medium">Mercoledì</span><span>9-12 · <em className="text-labor-terracotta">Avv. 16:30-18:30</em></span></li>
              <li className="flex justify-between border-b border-labor-border pb-2"><span className="font-medium">Giovedì</span><span>16:00 - 18:30</span></li>
              <li className="flex justify-between border-b border-labor-border pb-2"><span className="font-medium">Venerdì</span><span>16:00 - 18:30</span></li>
              <li className="flex justify-between text-labor-muted"><span className="font-medium">Sab · Dom</span><span>Chiuso</span></li>
            </ul>
            <a href="tel:+393791466566" className="mt-5 flex items-center justify-center gap-2 border-2 border-labor-olive text-labor-olive font-semibold py-2.5 hover:bg-labor-olive hover:text-white transition-colors">
              📞 379 146 6566 · solo urgenze
            </a>
          </div>
        </div>
      </section>

      {/* PERCHÉ NOI - trust bar */}
      <section className="bg-labor-olive text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-4 gap-6">
          {[
            { icon: Award, k: "Esperienza", v: "Anni al servizio dei bitontini" },
            { icon: Lock, k: "Riservatezza", v: "Dati trattati nel rispetto GDPR" },
            { icon: HeartHandshake, k: "Rapporto umano", v: "Ogni pratica seguita di persona" },
            { icon: Clock, k: "Puntualità", v: "Rispettiamo i tuoi tempi, chiediamo il rispetto dei nostri" },
          ].map((t, i) => (
            <div key={i} className="flex gap-4 items-start">
              <t.icon className="w-8 h-8 flex-shrink-0 text-labor-italianRed" strokeWidth={1.5} />
              <div>
                <div className="font-display text-xl">{t.k}</div>
                <div className="text-sm opacity-85 mt-1">{t.v}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVIZI PRINCIPALI */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-labor-muted">Cosa facciamo</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2">Servizi principali</h2>
          </div>
          <Link to="/servizi" className="text-labor-olive font-semibold hover:text-labor-terracotta">Vedi tutti i servizi →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: FileText, title: "Modello 730", desc: "Dichiarazione dei redditi. Rimborsi in tempi rapidi in busta paga o direttamente sul conto." },
            { icon: ShieldCheck, title: "ISEE / DSU", desc: "Attestazioni per bonus, assegni, agevolazioni, mensa scolastica, università." },
            { icon: Users, title: "Pensioni & INPS", desc: "Domande di pensione, ricostituzioni, RED, invalidità civile, NASpI, dimissioni telematiche." },
            { icon: FileText, title: "Assistenza CIE (tesserati)", desc: "Assistenza per PIN, PUK e credenziali della Carta d'Identità Elettronica." },
            { icon: MessageSquare, title: "Labor AI · assistente virtuale", desc: "Chatta con l'IA nell'app per info immediate. Se non sa, ti aiuta a prenotare." },
            { icon: CalendarCheck, title: "Prenotazione online", desc: "Niente file allo sportello: scegli data, ora e operatore comodamente da casa." },
          ].map((s, i) => (
            <div key={i} data-testid={`home-service-card-${i}`} className="paper-card p-7 hover:border-b-labor-olive transition-colors">
              <s.icon className="w-8 h-8 text-labor-terracotta mb-4" />
              <h3 className="font-display text-xl mb-1.5">{s.title}</h3>
              <p className="text-labor-muted text-[15px] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AREA LEGALE - highlight */}
      <section className="bg-labor-skyBg border-y border-labor-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 bg-labor-ink text-white px-3 py-1 text-xs uppercase tracking-widest font-semibold">
              <Scale className="w-3.5 h-3.5" /> Area Legale
            </div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
              Consulenza dell'<em className="text-labor-terracotta not-italic">Avvocato</em><br />
              tutti i mercoledì pomeriggio.
            </h2>
            <p className="mt-5 text-lg text-labor-muted max-w-2xl leading-relaxed">
              Presso il nostro ufficio è disponibile un professionista abilitato che riceve <strong className="text-labor-ink">ogni mercoledì dalle 16:30 alle 19:00</strong> per consulenze in materia civile, previdenziale, del lavoro e successioni. Il primo consulto orientativo è pensato per farti capire subito cosa fare e con quali costi.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-[15px]">
              <li className="flex items-start gap-2"><span className="text-labor-italianGreen mt-1">✓</span> Diritto del lavoro</li>
              <li className="flex items-start gap-2"><span className="text-labor-italianGreen mt-1">✓</span> Diritto di famiglia</li>
              <li className="flex items-start gap-2"><span className="text-labor-italianGreen mt-1">✓</span> Successioni e testamenti</li>
              <li className="flex items-start gap-2"><span className="text-labor-italianGreen mt-1">✓</span> Cause previdenziali INPS</li>
              <li className="flex items-start gap-2"><span className="text-labor-italianGreen mt-1">✓</span> Recupero crediti</li>
              <li className="flex items-start gap-2"><span className="text-labor-italianGreen mt-1">✓</span> Assistenza contrattuale</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/prenota?service=legale" data-testid="home-cta-legale" className="olive-btn px-6 py-3.5 font-semibold flex items-center gap-2">
                <CalendarCheck className="w-5 h-5" /> Prenota consulenza legale
              </Link>
              <a href="tel:+393791466566" className="border-2 border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white transition-colors px-6 py-3.5 font-semibold flex items-center gap-2">
                <Phone className="w-4 h-4" /> Chiama per info
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="paper-card p-8 bg-white relative">
              <div className="absolute top-0 right-0 bottom-0 w-1 bg-labor-ink"></div>
              <Scale className="w-16 h-16 text-labor-ink mb-5" strokeWidth={1.2} />
              <div className="text-xs uppercase tracking-widest text-labor-muted mb-2">Il tuo appuntamento</div>
              <div className="font-display text-3xl leading-tight">Consulenza di 45 minuti</div>
              <div className="mt-4 space-y-2 text-[15px]">
                <div className="flex justify-between border-b border-labor-border pb-2"><span className="text-labor-muted">Giorno</span><span className="font-medium">Solo mercoledì</span></div>
                <div className="flex justify-between border-b border-labor-border pb-2"><span className="text-labor-muted">Fascia</span><span className="font-medium">16:30 - 19:00</span></div>
                <div className="flex justify-between border-b border-labor-border pb-2"><span className="text-labor-muted">Durata</span><span className="font-medium">30 min</span></div>
                <div className="flex justify-between"><span className="text-labor-muted">Sede</span><span className="font-medium">Bitonto (BA)</span></div>
              </div>
              <div className="mt-5 p-3 bg-labor-skyBg text-[13px] text-labor-muted border-l-4 border-labor-terracotta">
                Riservatezza garantita. Ogni consulto è confidenziale.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHI SIAMO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-widest text-labor-muted">Chi siamo</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2">Vito e Marianna,<br/>due volti che conosci.</h2>
            <p className="mt-5 text-lg text-labor-muted leading-relaxed">
              Labor è nato per essere quello che un CAF dovrebbe essere: un posto dove <strong className="text-labor-ink">parli con una persona</strong>, non con un numero. Se hai una domanda, la risposta è chiara. Se hai una scadenza, la rispettiamo. Se hai un problema, lo risolviamo insieme.
            </p>
            <div className="mt-6 flex items-center gap-2 text-labor-muted text-sm">
              <MapPin className="w-4 h-4 text-labor-terracotta" />
              Bitonto, nel cuore della Puglia
            </div>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
            <div className="paper-card p-6">
              <div className="w-14 h-14 bg-labor-olive text-white flex items-center justify-center font-display text-2xl mb-4">V</div>
              <div className="font-display text-xl">Vito</div>
            </div>
            <div className="paper-card p-6">
              <div className="w-14 h-14 bg-labor-terracotta text-white flex items-center justify-center font-display text-2xl mb-4">M</div>
              <div className="font-display text-xl">Marianna</div>
            </div>
            <div className="paper-card p-6 sm:col-span-2 bg-labor-ink text-white">
              <div className="w-14 h-14 bg-white text-labor-ink flex items-center justify-center mb-4">
                <Scale className="w-7 h-7" strokeWidth={1.5} />
              </div>
              <div className="font-display text-xl">Avvocato · Area Legale</div>
              <div className="text-xs uppercase tracking-widest opacity-70 mt-1">Consulenze mercoledì pomeriggio</div>
              <p className="text-[15px] opacity-85 mt-3 leading-relaxed">Professionista abilitato che collabora con il CAF Labor per offrirti un supporto legale competente e riservato, direttamente presso il nostro ufficio.</p>
            </div>
          </div>
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section className="bg-labor-skyBg border-y border-labor-border">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <div className="text-xs uppercase tracking-widest text-labor-muted">Ultime</div>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <h2 className="font-display text-4xl md:text-5xl mt-2">Newsletter CAF Labor</h2>
              <Link to="/news" className="text-labor-olive font-semibold hover:text-labor-terracotta">Tutta la newsletter →</Link>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {news.slice(0, 2).map((n) => (
                <div key={n.id} className="paper-card p-7 bg-white">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-labor-muted mb-2">
                    {n.pinned && <span className="bg-labor-terracotta text-white px-2 py-0.5">IMPORTANTE</span>}
                    <span>{new Date(n.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <h3 className="font-display text-2xl mb-3">{n.title}</h3>
                  <p className="text-labor-muted text-[15px] leading-relaxed whitespace-pre-line">{n.body.slice(0, 220)}{n.body.length > 220 ? "…" : ""}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINALE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 my-16">
        <div className="paper-card p-10 md:p-16 bg-labor-olive text-white border-b-labor-terracotta" style={{ borderBottomWidth: 6 }}>
          <div className="grid md:grid-cols-2 items-center gap-8">
            <div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">Un passo alla volta,<br />insieme.</h2>
              <p className="mt-4 opacity-90 text-lg">Prenota online in meno di 60 secondi. Riceverai la conferma via email. Nessuna coda allo sportello.</p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <Link to="/prenota" data-testid="home-cta-prenota-bottom" className="terracotta-btn px-7 py-4 font-semibold flex items-center gap-2 text-lg">
                <CalendarCheck className="w-5 h-5" /> Prenota adesso
              </Link>
              <a href="tel:+393791466566" className="border-2 border-white text-white hover:bg-white hover:text-labor-olive transition-colors px-7 py-4 font-semibold flex items-center gap-2 text-lg">
                <Phone className="w-4 h-4" /> Chiama
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
